import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import ChannelSelectorModal from './ChannelSelectorModal';
import './ChannelWidgetView.css';

export const CHANNEL_WIDGET_DEFAULT_CONTEXT = Object.freeze({
  channelId: null,
  channelSource: null,
  showTimestamp: false,
});

const ChannelWidgetEdit = ({ context, onChange, errors }) => {
  const {
    channelId = null,
    channelSource = null,
    showTimestamp = false,
  } = context;

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (context.channelId === undefined) {
      onChange({ ...CHANNEL_WIDGET_DEFAULT_CONTEXT, ...context });
    }
  }, []);

  const handleSelectChannel = ({ raw, row }) => {
    const baseChannelName = row.channelName || `Channel ${raw.channelId}`;
    const channelType = row.sensorType || row.channelType || '';
    const channelName = channelType && !baseChannelName.includes(channelType)
      ? `${baseChannelName} (${channelType})`
      : baseChannelName;

    onChange({
      ...context,
      channelId: raw.channelId != null ? String(raw.channelId) : null,
      channelSource: {
        deviceName: row.deviceName || '',
        sensorName: row.sensorName || '',
        componentName: row.componentName || '',
        channelName,
      },
    });
    setIsModalOpen(false);
  };

  const hasChannel = !!channelId;

  const parts = hasChannel ? [
    channelSource && channelSource.deviceName,
    channelSource && channelSource.sensorName,
    channelSource && channelSource.componentName,
    (channelSource && channelSource.channelName) || `Channel ${channelId}`,
  ].filter(Boolean) : [];

  return (
    <FormErrorHandler errorList={errors} noDivTags>
      <Form.Group>
        <Form.Label>Channel</Form.Label>

        {hasChannel ? (
          <div className="d-flex align-items-center border rounded bg-white px-3 py-2">
            <div className="d-flex flex-wrap align-items-baseline flex-grow-1 mr-3" style={{ minWidth: 0, gap: '6px 10px' }}>
              {parts.map((text, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-muted small" aria-hidden="true">•</span>}
                  <span className="text-truncate" title={text}>{text}</span>
                </React.Fragment>
              ))}
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setIsModalOpen(true)}
            >
              Change Channel
            </Button>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between border border-dashed rounded px-3 py-2 bg-light">
            <span className="text-muted small">No channel selected</span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsModalOpen(true)}
            >
              Select Channel
            </Button>
          </div>
        )}

        {!hasChannel && (
          <Form.Text className="text-danger">Must select a channel.</Form.Text>
        )}
      </Form.Group>

      <Form.Group>
        <Form.Check
          type="checkbox"
          id="channel-widget-show-timestamp"
          label="Show reading timestamp"
          checked={!!showTimestamp}
          onChange={(e) => onChange({ ...context, showTimestamp: e.target.checked })}
        />
      </Form.Group>

      <ChannelSelectorModal
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        selectedChannelId={channelId}
        onSelectChannel={handleSelectChannel}
      />
    </FormErrorHandler>
  );
};

ChannelWidgetEdit.propTypes = {
  context: PropTypes.shape({
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    channelSource: PropTypes.shape({
      deviceName: PropTypes.string,
      sensorName: PropTypes.string,
      channelName: PropTypes.string,
    }),
    showTimestamp: PropTypes.bool,
  }),
  onChange: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
    code: PropTypes.string,
  })),
};

ChannelWidgetEdit.defaultProps = {
  context: {},
  onChange: () => {},
  errors: [],
};

export default ChannelWidgetEdit;
